
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Archive, RotateCcw, Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FeedbackDialog } from "./FeedbackDialog";
import { ApprovalCardProps } from "@/types/approval";

export function ApprovalCard({
  id,
  title,
  image,
  clientName,
  caption,
  post_type,
  carousel_images,
  reels_video,
  onApprove,
  onReject,
  onArchive,
  onRestore,
  isArchivedView = false,
  getBaseDomain
}: ApprovalCardProps) {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const handleReject = (feedback: string) => {
    onReject(id, feedback);
  };

  const handleApprove = (feedback?: string) => {
    onApprove(id, feedback);
  };

  const renderMedia = () => {
    if (post_type === 'carousel' && carousel_images && carousel_images.length > 0) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {carousel_images.slice(0, 4).map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Carousel ${index + 1}`}
              className="w-full h-24 object-cover rounded"
            />
          ))}
          {carousel_images.length > 4 && (
            <div className="w-full h-24 bg-gray-100 rounded flex items-center justify-center text-sm text-gray-600">
              +{carousel_images.length - 4} mais
            </div>
          )}
        </div>
      );
    }

    if (post_type === 'reels' && reels_video) {
      return (
        <video
          src={reels_video}
          className="w-full h-48 object-cover rounded"
          controls
        />
      );
    }

    if (image) {
      return (
        <img
          src={image}
          alt={title}
          className="w-full h-48 object-cover rounded"
        />
      );
    }

    return null;
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{clientName}</p>
            </div>
            <Badge variant="outline" className="capitalize">
              {post_type || 'static'}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {renderMedia()}
          
          {caption && (
            <p className="text-sm text-gray-600 line-clamp-3">{caption}</p>
          )}
          
          <div className="flex gap-2 flex-wrap">
            {!isArchivedView ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                  onClick={() => setApproveDialogOpen(true)}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Aprovar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setFeedbackDialogOpen(true)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Rejeitar
                </Button>
                {onArchive && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onArchive(id)}
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    Arquivar
                  </Button>
                )}
              </>
            ) : (
              onRestore && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRestore(id)}
                  className="w-full"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restaurar
                </Button>
              )
            )}
          </div>
        </CardContent>
      </Card>

      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        onSubmit={handleReject}
        title="Rejeitar Post"
        description="Por favor, forneça um feedback sobre por que este post está sendo rejeitado."
        submitText="Rejeitar"
        required={true}
      />

      <FeedbackDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        onSubmit={handleApprove}
        title="Aprovar Post"
        description="Você pode adicionar um feedback opcional para este post aprovado."
        submitText="Aprovar"
        required={false}
      />
    </>
  );
}
